// ================================
// IMPORTS
// ================================
import { computed, type Ref } from "vue"
import { useAuthStore } from "@core/stores/auth.store"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Retour du composable useCompanySourceLimits
 */
export interface UseCompanySourceLimitsReturn {
    /**
     * Nombre maximum de sources autorisées selon l'abonnement
     */
    getMaxSubscriptionSourceCount: Readonly<Ref<number>>
    /**
     * Nombre de sources actives actuellement
     */
    getActiveSourceCount: Readonly<Ref<number>>
    /**
     * Indique si la limite de sources est déjà atteinte (avant ajout)
     */
    getIsLimitReached: Readonly<Ref<boolean>>
    /**
     * Indique si l'ajout de nouvelles sources dépasserait strictement la limite
     * (un total égal au plafond n'est pas considéré comme un dépassement).
     * Fonction pure : l'appeler depuis un computed pour suivre actifs / plafond.
     * @param additionalSourcesCount - Nombre de sources supplémentaires à ajouter
     */
    getWouldExceedLimit: (additionalSourcesCount: number) => boolean
}

// ================================
// COMPOSABLE
// ================================

/**
 * Composable pour gérer les limites de sources d'une entreprise
 * Calcule le nombre maximum de sources autorisées et le nombre actif
 * depuis les informations de l'entreprise dans le store auth
 *
 * @returns Objet contenant les computed pour les limites de sources
 */
export function useCompanySourceLimits(): UseCompanySourceLimitsReturn {
    const authStore = useAuthStore()

    // ================================
    // DONNÉES CALCULÉES
    // ================================

    /**
     * Nombre maximum de sources autorisées selon les abonnements Stripe actifs
     * Calcule la somme des métadonnées "sources" de toutes les subscriptions non annulées
     */
    const getMaxSubscriptionSourceCount = computed(() => {
        const subscriptions =
            authStore.company?.operation?.stripe?.subscriptions ??
            authStore.companyStripeSubscriptions ??
            []
        let total = 0

        for (const subscription of subscriptions) {
            // Ignorer les abonnements annulés
            if (subscription.status === "canceled") continue

            // Ajouter le nombre de sources depuis les métadonnées
            if (subscription.metadata?.sources) {
                const sourcesCount = parseInt(
                    subscription.metadata.sources as string,
                    10
                )
                if (!isNaN(sourcesCount)) {
                    total += sourcesCount
                }
            }
        }

        return total
    })

    /**
     * Nombre de sources actives actuellement
     * Récupéré depuis company.operation.count_active_source
     */
    const getActiveSourceCount = computed(() => {
        const fromCompany = (
            authStore.company?.operation as { count_active_source?: number }
        )?.count_active_source
        if (fromCompany != null) return fromCompany
        return authStore.companySourcesCount ?? 0
    })

    /**
     * Indique si la limite de sources est déjà atteinte (avant ajout de nouvelles sources)
     */
    const getIsLimitReached = computed(() => {
        return getActiveSourceCount.value >= getMaxSubscriptionSourceCount.value
    })

    /**
     * Indique si l'ajout de nouvelles sources ferait un total strictement supérieur au plafond
     * @param additionalSourcesCount - Nombre de sources supplémentaires à ajouter
     * @returns True si actifs + ajout > max, false sinon (y compris égal au plafond)
     */
    function getWouldExceedLimit(additionalSourcesCount: number): boolean {
        return (
            getActiveSourceCount.value + additionalSourcesCount >
            getMaxSubscriptionSourceCount.value
        )
    }

    return {
        getMaxSubscriptionSourceCount,
        getActiveSourceCount,
        getIsLimitReached,
        getWouldExceedLimit,
    }
}
